import { Component, OnInit, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiError } from '../../../abstract/api-error';
import { RemoveDashPipe } from '../../../shared/pipes/remove-dash.pipe';
import { DroneService } from '../../../core/data-services/drone.service';
import { Drone } from '../../../core/data-models/drone.model';
import { DroneStatus } from '../../../core/data-models/drone-status.model';
import { MaintenanceRecord } from '../../../core/data-models/maintenance-record.model';
import { CreateMaintenanceRecordDto } from '../../../core/request-models/create-maintenance-record-dto.model';
import { UpdateMaintenanceRecordDto } from '../../../core/request-models/update-maintenance-record-dto.model';
import { DroneReportConfirmModalComponent } from 'src/app/shared/components/drone-report-confirm-modal/drone-report-confirm-modal.component';
import { DroneReport } from '../../../core/data-models/drone-report.model';
import { DroneResource } from '../../../core/data-models/drone-resource.model';
import { DroneResourceService } from '../../../core/data-services/drone-resource.service';
import { FileUploadService } from '../../../core/api-services/file-upload.service';
import {
  DroneResourceModalComponent,
  DRONE_RESOURCE_TYPES
} from '../../../shared/components/drone-resource-modal/drone-resource-modal.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { MaintenanceModalComponent } from '../../../shared/components/maintenance-modal/maintenance-modal.component';
import { DateFormatPipe } from '../../../shared/pipes/date-format.pipe';
import { OrganizationContextService } from 'src/app/core/services/organization-context.service';
import { DroneReportActionResult, DroneReportService } from 'src/app/core/data-services/drone-report.service';
import { formatDateRange, toDateInputValue } from 'src/app/shared/utils/date.utils';
import { DroneReportItemComponent } from 'src/app/shared/components/drone-report-item/drone-report-item.component';
import { DjiConnectionCardComponent } from 'src/app/shared/components/dji-connection-card/dji-connection-card.component';
import { isDjiManufacturer } from 'src/app/shared/utils/dji-flight.utils';
import { isHttpsUrl, openExternal } from '../../../core/utils/external-link-utils';

@Component({
  selector: 'app-drone-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ConfirmModalComponent,
    MaintenanceModalComponent,
    DroneResourceModalComponent,
    RemoveDashPipe,
    DateFormatPipe,
    DroneReportItemComponent,
    DroneReportConfirmModalComponent,
    DjiConnectionCardComponent
  ],
  templateUrl: './drone-details.component.html',
  styleUrls: ['./drone-details.component.scss']
})
export class DroneDetailsComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;
  @ViewChild('maintenanceModal') maintenanceModal!: MaintenanceModalComponent;
  @ViewChild('resourceConfirmModal') resourceConfirmModal!: ConfirmModalComponent;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private droneService = inject(DroneService);
  private droneResourceService = inject(DroneResourceService);
  private fileUploadService = inject(FileUploadService);
  private organizationContext = inject(OrganizationContextService);
  private droneReportService = inject(DroneReportService);

  // ------ Drone State ------
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);
  droneId = signal<string | null>(null);
  drone = signal<Drone | null>(null);

  // Computed: drone stats from spray records
  readonly droneStats = computed(() => {
    const id = this.droneId();
    if (!id) {
      return { totalFlights: 0, totalAcresSprayed: 0, totalFlightHours: 0, lastFlightDate: undefined };
    }
    return this.droneService.getDroneFlightStats(id);
  });

  // Computed: has flight history
  readonly hasFlightHistory = computed(() => {
    return this.droneStats().totalFlights > 0;
  });

  // ------ Maintenance State ------
  // Computed: maintenance records for this drone
  readonly maintenanceRecords = computed(() => {
    const id = this.droneId();
    if (!id) return [];
    return this.droneService.getMaintenanceByDroneId(id);
  });

  // Computed: upcoming maintenance
  readonly upcomingMaintenance = computed(() => {
    const id = this.droneId();
    if (!id) return null;
    return this.droneService.getUpcomingMaintenance(id);
  });

  // Computed: recent maintenance (last 5)
  readonly recentMaintenance = computed(() => {
    return this.maintenanceRecords()
      .sort((a, b) => new Date(b.scheduledDate).getTime() - new Date(a.scheduledDate).getTime())
      .slice(0, 5);
  });

  // ------ Documents State ------
  resources = signal<DroneResource[]>([]);
  loadingResources = signal(false);
  resourcesError = signal<string | null>(null);
  resourceTypeFilter = signal('all');
  resourceToDelete = signal<DroneResource | null>(null);
  showResourceModal = signal(false);
  readonly resourceTypes = ['all', ...DRONE_RESOURCE_TYPES];

  // ------ Drone Reporting State ------
  readonly recentReports = computed(() => {
    const id = this.droneId();
    return this.droneReportService.reports().filter(r => r.droneId === id);
  });

  // Drone reports are restricted to Owner, Chief Supervisor and PIC (Pilot is excluded)
  readonly canGenerateReports = computed(() => this.droneReportService.canManageReports());

  readonly reportError = this.droneReportService.error;

  isGenerating = signal(false);

  reportStartDate = toDateInputValue(this.drone()?.createdAt ?? '');
  reportEndDate = toDateInputValue(new Date());

  // Report pending delete confirmation (the modal is open while this is set)
  reportToDelete = signal<DroneReport | null>(null);

  // ------ Lifecycle ------
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.droneId.set(id);
      this.loadDrone(id);
      this.loadResources(id);
    } else {
      this.errorMessage.set('No drone ID provided');
      this.isLoading.set(false);
    }
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.droneReportService.clearError();
  }

  /**
   * Retry loading reports after an error
   */
  retryLoad(): void {
    this.clearError();
    const drone = this.drone();
    if (drone)this.loadReports(drone);
  }

  // ------ Drone ------
  private async loadDrone(id: string): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      // First check local cache
      let found = this.droneService.getDroneById(id);

      // If not in cache, fetch from API (e.g., navigating from activity link)
      if (!found) {
        found = await this.droneService.fetchDroneById(id) ?? undefined;
      }

      if (found) {
        this.drone.set(found);
        this.resetForm();
        this.loadReports(found);
      } else {
        this.errorMessage.set('Drone not found');
      }
    } catch (error: any) {
      console.error('[DroneDetailsComponent] Error loading drone:', error);
      this.errorMessage.set('Failed to load drone');
    } finally {
      this.isLoading.set(false);
    }
  }

  // Permission checks
  canEdit(): boolean {
    const d = this.drone();
    return d ? this.droneService.canEdit(d) : false;
  }

  canDelete(): boolean {
    const d = this.drone();
    return d ? this.droneService.canDelete(d) : false;
  }

  /** DJI connection steps and status only make sense for DJI-built drones (ADN's Talos units are DJI Agras). */
  isDjiDrone(): boolean {
    return isDjiManufacturer(this.drone()?.manufacturerName);
  }

  isAdnDrone(): boolean {
    return this.drone()?.ownership === 'adn';
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/drones']);
  }

  editDrone(): void {
    const id = this.droneId();
    if (id && this.canEdit()) {
      this.router.navigate(['/drones', id, 'edit']);
    }
  }

  viewFlightHistory(): void {
    // Navigate to spray records filtered by this drone
    const id = this.droneId();
    if (id) {
      this.router.navigate(['/logbook/spray-records'], {
        queryParams: { droneId: id }
      });
    }
  }

  // Delete functionality
  confirmDelete(): void {
    const d = this.drone();
    if (d && this.canDelete()) {
      this.confirmModal.open({
        title: 'Delete Drone',
        message: `Are you sure you want to delete "${d.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonClass: 'btn-danger',
        icon: 'bi-trash'
      });
    }
  }

  async onDeleteConfirmed(): Promise<void> {
    const id = this.droneId();
    if (id) {
      try {
        await this.droneService.deleteDrone(id);
        this.router.navigate(['/drones']);
      } catch (error: any) {
        console.error('[DroneDetailsComponent] Error deleting drone:', error);
        alert(error.message || 'Failed to delete drone');
      }
    }
  }

  onDeleteCancelled(): void {
    // No action needed
  }

  // ------ Maintenance ------
  addMaintenanceRecord(): void {
    this.maintenanceModal.openCreate();
  }

  editMaintenanceRecord(record: MaintenanceRecord): void {
    this.maintenanceModal.openEdit(record);
  }

  onMaintenanceSaved(data: CreateMaintenanceRecordDto | { id: string; dto: UpdateMaintenanceRecordDto }): void {
    if ('id' in data) {
      // Update existing record
      this.droneService.updateMaintenanceRecord(data.id, data.dto);
    } else {
      // Create new record
      this.droneService.createMaintenanceRecord(data);
    }
  }

  onMaintenanceCancelled(): void {
    // No action needed
  }

  // ------ Documents ------
  async loadResources(id?: string): Promise<void> {
    const droneId = id ?? this.droneId();
    if (!droneId) return;

    this.loadingResources.set(true);
    this.resourcesError.set(null);

    const filter = this.resourceTypeFilter();

    try {
      this.resources.set(
        await this.droneResourceService.getResourcesByDroneId(
          droneId,
          filter === 'all' ? undefined : filter
        )
      );
    } catch (error: any) {
      console.error('Failed to load drone documents:', error);
      this.resourcesError.set('Could not load documents for this drone.');
    } finally {
      this.loadingResources.set(false);
    }
  }

  openResourceModal(): void {
    this.showResourceModal.set(true);
  }

  closeResourceModal(): void {
    this.showResourceModal.set(false);
  }

  /** Reset the filter so a just-uploaded file is guaranteed visible. */
  onResourceUploaded(): void {
    this.showResourceModal.set(false);
    this.resourceTypeFilter.set('all');
    this.loadResources();
  }

  confirmResourceDelete(resource: DroneResource): void {
    this.resourceToDelete.set(resource);
    this.resourceConfirmModal.open({
      title: 'Delete document?',
      message: `"${resource.fileName}" will be removed from this drone.`,
      confirmText: 'Delete',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-trash'
    });
  }

  async onResourceDeleteConfirmed(): Promise<void> {
    const resource = this.resourceToDelete();
    const droneId = this.droneId();
    if (!resource || !droneId) return;

    try {
      await this.droneResourceService.deleteResource(droneId, resource.id);
      this.resourceToDelete.set(null);
      this.loadResources();
    } catch (error: unknown) {
      this.resourcesError.set(ApiError.fromHttpError(error).errors[0] ?? 'Could not delete that document.');
    }
  }

  /**
   * Resources are stored private, so the signed URL is fetched per click.
   * The tab is opened synchronously so popup blockers don't eat it after the
   * await, then pointed at the URL once it resolves.
   */
  async openResource(resource: DroneResource): Promise<void> {
    const id = this.droneId();
    if (!id) return;

    const tab = window.open('', '_blank');
    // Sever the back-reference so the opened page can't navigate this one.
    if (tab) tab.opener = null;

    try {
      const url = await this.droneResourceService.getResourceDownloadUrl(id, resource.id);
      if (!isHttpsUrl(url)) {
        throw new Error('Refused a non-https resource URL');
      }
      if (tab) {
        tab.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (error: any) {
      tab?.close();
      console.error('Failed to open drone document:', error);
      this.resourcesError.set('Could not open that document.');
    }
  }

  formatFileSize(bytes: number | null | undefined): string {
    if (bytes === null || bytes === undefined) return '';
    return this.fileUploadService.formatFileSize(bytes);
  }

  // ------ Drone Reporting ------

  private async loadReports(drone: Drone): Promise<void> {
    if (!this.canGenerateReports()) return;

    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('No organization ID available, cannot load Drone reports');
      return;
    }

    await this.droneReportService.loadReports(orgId, {
      droneId: drone.id,
      droneSource: drone.ownership
    });
  }

  /**
   * Execute generation after confirmation
   */
  async generateReport(): Promise<void> {
    if (!this.isDateRangeValid()) return;
    this.isGenerating.set(true);

    try {
      // Create the report request
      const report = await this.droneReportService.startReport({
        name: this.getReportName(),
        ...this.getReportDates(),
        droneId: this.droneId() ?? '',
        droneSource: this.drone()?.ownership ?? 'organization',
        scope: 'full_history',
      });

      if (report) {
        // Reset form after successful generation start
        this.resetForm();
      }
    } finally {
      this.isGenerating.set(false);
    }
  }

  /**
   * Download report PDF
   * Gets fresh pre-signed URL and opens in new tab
   */
  async downloadReport(report: DroneReport): Promise<void> {
    const url = await this.droneReportService.getDownloadUrl(report);
    if (url) {
      openExternal(url);
    }
  }

  /**
   * Retry a failed report
   */
  async retryReport(report: DroneReport): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    if (report.status !== 'failed') return;

    await this.droneReportService.retryFailedReport(orgId, report.id);
  }

  readonly deleteReport = async (report: DroneReport): Promise<DroneReportActionResult> => {
    return this.droneReportService.removeReport(report);
  };

  // Report form helpers
  /**
   * Validate date range
   */
  isDateRangeValid(): boolean {
    if (!this.reportStartDate || !this.reportEndDate) {
      return false;
    }
    const start = new Date(this.reportStartDate);
    const end = new Date(this.reportEndDate);
    return end >= start;
  }

  /**
   * Reset the generate report form to its defaults (drone created date → today)
   */
  resetForm(): void {
    const { startDate, endDate } = this.getDefaultReportDates();
    this.reportStartDate = startDate;
    this.reportEndDate = endDate;
  }

  /**
   * Default report range: the drone's created date through today, as "YYYY-MM-DD"
   */
  private getDefaultReportDates(): { startDate: string; endDate: string } {
    return {
      startDate: toDateInputValue(this.drone()?.createdAt),
      endDate: toDateInputValue(new Date()),
    };
  }

  /**
   * Generate report name based on drone and date range (month granularity),
   * e.g. "Mavic 3 Report - Sep 2026 to Oct 2026"
   */
  /**
   * The report's date range, falling back to the defaults for any input the user cleared
   */
  private getReportDates(): { startDate: string; endDate: string } {
    const defaults = this.getDefaultReportDates();
    return {
      startDate: this.reportStartDate || defaults.startDate,
      endDate: this.reportEndDate || defaults.endDate,
    };
  }

  private getReportName(): string {
    const droneName = this.drone()?.name ?? 'Drone';
    const { startDate, endDate } = this.getReportDates();
    const months = formatDateRange(startDate, endDate, {
      format: { month: 'short', year: 'numeric' },
      separator: ' to '
    });
    return months ? `${droneName} Report - ${months}` : `${droneName} Report`;
  }

  // ------ Formatting Helpers ------
  formatStatus(status: DroneStatus | null | undefined): string {
    if (!status) return '—';
    const labels: Record<DroneStatus, string> = {
      'active': 'Active',
      'maintenance': 'Maintenance',
      'inactive': 'Inactive',
      'retired': 'Retired'
    };
    return labels[status] || status;
  }

  getStatusClass(status: DroneStatus | null | undefined): string {
    if (!status) return 'bg-secondary';
    const classes: Record<DroneStatus, string> = {
      'active': 'bg-success',
      'maintenance': 'bg-warning text-dark',
      'inactive': 'bg-secondary',
      'retired': 'bg-dark'
    };
    return classes[status] || 'bg-secondary';
  }

  formatOwnership(ownership: 'adn' | 'organization'): string {
    return ownership === 'adn' ? 'ADN Leased' : 'Organization Owned';
  }

  getOwnershipClass(ownership: 'adn' | 'organization'): string {
    return ownership === 'adn'
      ? 'bg-primary-subtle text-primary'
      : 'bg-success-subtle text-success';
  }

  formatMaintenanceType(type: string): string {
    const labels: Record<string, string> = {
      'scheduled': 'Scheduled',
      'repair': 'Repair',
      'inspection': 'Inspection',
      'upgrade': 'Upgrade',
      'other': 'Other'
    };
    return labels[type] || type;
  }

  formatMaintenanceStatus(status: string): string {
    const labels: Record<string, string> = {
      'scheduled': 'Scheduled',
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  }

  getMaintenanceStatusClass(status: string): string {
    const classes: Record<string, string> = {
      'scheduled': 'bg-info-subtle text-info',
      'in_progress': 'bg-warning-subtle text-warning',
      'completed': 'bg-success-subtle text-success',
      'cancelled': 'bg-secondary-subtle text-secondary'
    };
    return classes[status] || 'bg-secondary';
  }

  formatDate(date: string | null | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  }
}
